/**
 * useNotificationsRealtime - Supabase realtime subscription for notifications
 *
 * Phase 3.1c - Push updates into React Query cache
 *
 * Features:
 * - Subscribe to postgres_changes on notifications table
 * - On INSERT: route to notifications cache (system) OR inbox cache (recommendation/list_shared)
 * - On UPDATE (is_read): update appropriate cache based on type
 * - Lazy connect: only subscribe when user is logged in
 * - Single channel per user (10M scale requirement)
 *
 * IMPORTANT: Inbox types (recommendation, list_shared) are routed to inbox cache, NOT notifications.
 * This prevents double-counting between Notifications bell and Inbox icon.
 *
 * Note: This hook handles cache updates internally.
 * Call it from NotificationProvider to enable realtime.
 */

import { useEffect, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { supabase } from '../lib/supabase';
import { useAuth } from '../contexts/AuthContext';
import { queryKeys } from '../lib/queryKeys';
import type { Notification } from './useNotificationsQuery';
import type { RealtimeChannel, RealtimePostgresInsertPayload, RealtimePostgresUpdatePayload } from '@supabase/supabase-js';

// Inbox types - routed to inbox cache, NOT notifications cache
const INBOX_TYPES: NotificationRow['type'][] = ['recommendation', 'list_shared'];

// Database row type (snake_case from Supabase)
interface NotificationRow {
  id: string;
  user_id: string;
  type: 'new_follower' | 'recommendation' | 'milestone' | 'list_shared';
  body: string;
  data: Record<string, unknown> | null;
  is_read: boolean;
  read_at: string | null;
  created_at: string;
  from_user_id: string | null;
}

// Response type for list queries
interface NotificationsListResponse {
  notifications: Notification[];
  next_cursor: string | null;
  has_more: boolean;
}

/**
 * Hook to subscribe to realtime notifications.
 * Call this once in NotificationProvider to enable realtime updates.
 */
export function useNotificationsRealtime(): void {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const channelRef = useRef<RealtimeChannel | null>(null);

  useEffect(() => {
    const userId = user?.id;
    if (!userId) {
      // Clean up any existing subscription when user logs out
      if (channelRef.current) {
        supabase.removeChannel(channelRef.current);
        channelRef.current = null;
      }
      return;
    }

    // Create single channel for this user's notifications
    const channel = supabase
      .channel(`notifications:${userId}`)
      .on<NotificationRow>(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'notifications',
          filter: `user_id=eq.${userId}`,
        },
        (payload: RealtimePostgresInsertPayload<NotificationRow>) => {
          handleInsert(payload.new, userId, queryClient);
        }
      )
      .on<NotificationRow>(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'notifications',
          filter: `user_id=eq.${userId}`,
        },
        (payload: RealtimePostgresUpdatePayload<NotificationRow>) => {
          handleUpdate(payload.old, payload.new, userId, queryClient);
        }
      )
      .subscribe((status, err) => {
        if (status === 'SUBSCRIBED') {
          console.debug('[Realtime] Subscribed to notifications channel');
          return;
        }

        const errorCode =
          typeof err === 'object' &&
          err !== null &&
          'code' in err &&
          typeof (err as { code?: unknown }).code === 'string'
            ? (err as { code: string }).code
            : undefined;
        const errorMessage =
          typeof err === 'object' &&
          err !== null &&
          'message' in err &&
          typeof (err as { message?: unknown }).message === 'string'
            ? (err as { message: string }).message
            : undefined;
        const errorSuffix = [errorCode ? `code=${errorCode}` : null, errorMessage ? `message=${errorMessage}` : null]
          .filter(Boolean)
          .join(' ');
        const details = errorSuffix ? ` (${errorSuffix})` : '';

        if (status === 'CHANNEL_ERROR') {
          console.error(`[Realtime] Failed to subscribe to notifications channel${details}`);
        } else if (status === 'TIMED_OUT') {
          console.error(`[Realtime] Notifications channel subscription timed out${details}`);
        } else {
          console.warn(`[Realtime] Notifications channel status: ${status}${details}`);
        }
      });

    channelRef.current = channel;

    // Cleanup on unmount or user change
    return () => {
      if (channelRef.current) {
        supabase.removeChannel(channelRef.current);
        channelRef.current = null;
      }
    };
  }, [user?.id, queryClient]);
}

/**
 * Check if a notification exists in any list cache
 */
function findNotificationInCache(
  notificationId: string,
  userId: string,
  queryClient: ReturnType<typeof useQueryClient>
): Notification | null {
  const listBaseKey = queryKeys.notifications.listBase(userId);
  const queries = queryClient.getQueriesData<NotificationsListResponse | { pages: NotificationsListResponse[] }>({ queryKey: listBaseKey });

  for (const [, data] of queries) {
    if (!data) continue;

    if ('pages' in data) {
      // Infinite query
      for (const page of data.pages) {
        const found = page.notifications.find((n) => n.id === notificationId);
        if (found) return found;
      }
    } else {
      // Simple query
      const found = data.notifications.find((n) => n.id === notificationId);
      if (found) return found;
    }
  }

  return null;
}

/**
 * Handle INSERT: route to appropriate cache based on notification type
 *
 * IMPORTANT: Inbox types (recommendation, list_shared) go to inbox cache.
 * System types (new_follower, milestone) go to notifications cache.
 *
 * Issue B fix:
 * - Dedupe by id (skip if already in cache)
 * - Cap simple queries to their limit (bounded memory)
 * - Only patch first page of infinite queries with dedupe
 */
function handleInsert(
  row: NotificationRow,
  userId: string,
  queryClient: ReturnType<typeof useQueryClient>
): void {
  // Route inbox types to inbox cache, not notifications cache
  if (INBOX_TYPES.includes(row.type)) {
    handleInboxInsert(row, userId, queryClient);
    return;
  }

  // System notification: route to notifications cache
  // Dedupe: skip if notification already exists in cache
  if (findNotificationInCache(row.id, userId, queryClient)) {
    return;
  }

  // Convert row to Notification shape
  // Note: from_user won't be populated via realtime, but body should contain the info
  const notification: Notification = {
    id: row.id,
    type: row.type,
    body: row.body,
    data: row.data,
    is_read: row.is_read,
    read_at: row.read_at,
    created_at: row.created_at,
    from_user: null, // Realtime doesn't join - will be populated on next fetch
  };

  // Increment unread count if notification is unread
  if (!row.is_read) {
    const countKey = queryKeys.notifications.unreadCount(userId);
    queryClient.setQueryData<number>(countKey, (old) => (old ?? 0) + 1);
  }

  // Prepend to all notification list caches with dedupe and cap
  const listBaseKey = queryKeys.notifications.listBase(userId);
  queryClient.getQueriesData<NotificationsListResponse | { pages: NotificationsListResponse[] }>({ queryKey: listBaseKey }).forEach(([key, data]) => {
    if (data) {
      if ('pages' in data) {
        // Infinite query - prepend to first page only, with dedupe
        queryClient.setQueryData<{ pages: NotificationsListResponse[]; pageParams: unknown[] }>(key, (old) => {
          if (!old || old.pages.length === 0) return old;
          const firstPage = old.pages[0];
          // Dedupe: filter out if already exists
          const deduped = firstPage.notifications.filter((n) => n.id !== notification.id);
          return {
            ...old,
            pages: [
              {
                ...firstPage,
                notifications: [notification, ...deduped],
              },
              ...old.pages.slice(1),
            ],
          };
        });
      } else {
        // Simple query - prepend with dedupe and cap to limit
        // Extract limit from query key: ['notifications', userId, 'list', { limit, ... }]
        const keyParams = key[3] as { limit?: number } | undefined;
        const limit = keyParams?.limit ?? 10;

        queryClient.setQueryData<NotificationsListResponse>(key, (old) => {
          if (!old) return old;
          // Dedupe: filter out if already exists
          const deduped = old.notifications.filter((n) => n.id !== notification.id);
          // Prepend and cap to limit
          const updated = [notification, ...deduped].slice(0, limit);
          return {
            ...old,
            notifications: updated,
          };
        });
      }
    }
  });
}

/**
 * Handle INSERT for inbox types (recommendation, list_shared)
 * Updates inbox unread count cache only (thread list will refetch)
 */
function handleInboxInsert(
  row: NotificationRow,
  userId: string,
  queryClient: ReturnType<typeof useQueryClient>
): void {
  // Increment inbox unread count if notification is unread
  if (!row.is_read) {
    const inboxCountKey = queryKeys.inbox.unreadCount(userId);
    queryClient.setQueryData<number>(inboxCountKey, (old) => (old ?? 0) + 1);
  }

  // Invalidate inbox threads to trigger refetch (server-side grouping is complex)
  queryClient.invalidateQueries({ queryKey: queryKeys.inbox.threadsBase(userId) });
}

/**
 * Handle UPDATE: route to appropriate cache based on notification type
 *
 * IMPORTANT: Inbox types (recommendation, list_shared) update inbox cache.
 * System types (new_follower, milestone) update notifications cache.
 *
 * Issue A fix:
 * - Before decrementing unreadCount, check the React Query cache
 * - Only decrement if cache currently shows is_read=false AND newRow.is_read=true
 * - This prevents double-decrement when optimistic update already applied
 */
function handleUpdate(
  _oldRow: Partial<NotificationRow>,
  newRow: NotificationRow,
  userId: string,
  queryClient: ReturnType<typeof useQueryClient>
): void {
  // Route inbox types to inbox cache update
  if (INBOX_TYPES.includes(newRow.type)) {
    handleInboxUpdate(newRow, userId, queryClient);
    return;
  }

  // System notification: update notifications cache
  // Issue A fix: Check cache state, not oldRow from realtime
  // The optimistic update may have already marked this as read
  const cachedNotification = findNotificationInCache(newRow.id, userId, queryClient);
  const cacheShowsUnread = cachedNotification?.is_read === false;
  const isNowRead = newRow.is_read === true;

  // Only decrement if cache shows unread AND realtime says now read
  if (cacheShowsUnread && isNowRead) {
    const countKey = queryKeys.notifications.unreadCount(userId);
    queryClient.setQueryData<number>(countKey, (old) => Math.max(0, (old ?? 0) - 1));
  }

  // Update the notification in all list caches
  const listBaseKey = queryKeys.notifications.listBase(userId);
  queryClient.getQueriesData<NotificationsListResponse | { pages: NotificationsListResponse[] }>({ queryKey: listBaseKey }).forEach(([key, data]) => {
    if (data) {
      if ('pages' in data) {
        // Infinite query
        queryClient.setQueryData<{ pages: NotificationsListResponse[]; pageParams: unknown[] }>(key, (old) => {
          if (!old) return old;
          return {
            ...old,
            pages: old.pages.map((page) => ({
              ...page,
              notifications: page.notifications.map((n) =>
                n.id === newRow.id ? { ...n, is_read: newRow.is_read, read_at: newRow.read_at } : n
              ),
            })),
          };
        });
      } else {
        // Simple query
        queryClient.setQueryData<NotificationsListResponse>(key, (old) => {
          if (!old) return old;
          return {
            ...old,
            notifications: old.notifications.map((n) =>
              n.id === newRow.id ? { ...n, is_read: newRow.is_read, read_at: newRow.read_at } : n
            ),
          };
        });
      }
    }
  });
}

/**
 * Handle UPDATE for inbox types (recommendation, list_shared)
 * Invalidates inbox queries to refetch (simpler than tracking unread state)
 */
function handleInboxUpdate(
  newRow: NotificationRow,
  userId: string,
  queryClient: ReturnType<typeof useQueryClient>
): void {
  // If marked as read, decrement inbox unread count
  if (newRow.is_read === true) {
    const inboxCountKey = queryKeys.inbox.unreadCount(userId);
    queryClient.setQueryData<number>(inboxCountKey, (old) => Math.max(0, (old ?? 0) - 1));
  }

  // Invalidate inbox threads to refresh unread counts per sender
  queryClient.invalidateQueries({ queryKey: queryKeys.inbox.threadsBase(userId) });
}
